import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { v4 as getUUID } from 'uuid';
import T from 'i18n-react';
import { Dropzone, Loader, FilePicker, EmptyState } from '@adsk/bim360-matrix-react-components';
import classNames from 'classnames';
import { issueAttributesType } from '../../../constants/proptype-validation';
import { canAddAttachment } from '../../../utils/issues-helpers';
import { filterCurrentDocument, getAllowedFiles } from '../../../utils/attachmentsHelper';
import {
  createGetFolderContentsMethod,
  createHasMoreFolderContents,
} from '../../../utils/docsApiHelpers';
import IssueAttachmentsList from '../../IssueAttachmentsList';
import { viewTypes } from '../../../constants/view-types';
import { attachmentOrigin } from '../../../constants/attachment-types';
import { connect } from '../../../redux/connect';
import {
  fetchAttachmentsAction,
  fetchProjectFolders,
  fetchFolderContents,
  createAttachmentFromDocument,
  clearFailedUploadingAttachments,
} from '../../../redux/actions';
import {
  isLoadingAttachments,
  getAttachmentsList,
  getUploadingAttachments,
  getCurrentViewType,
} from '../../../redux/selectors';
import AttachmentUploadThunk from '../../../redux/thunk/attachmentUploadThunk';

class IssuePreviewAttachmentsTab extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isSaving: false,
    };

    this.handleUploadFiles = this.handleUploadFiles.bind(this);
    this.handleChoosingDocument = this.handleChoosingDocument.bind(this);
    this.getProjectFolders = this.getProjectFolders.bind(this);
    this.getFolderContents = this.getFolderContents.bind(this);
    this.hasMoreFolderContents = this.hasMoreFolderContents.bind(this);
  }

  componentWillMount() {
    this.props.fetchAttachmentsAction(this.props.issue.id);
  }

  componentWillUnmount() {
    this.props.clearFailedUploadingAttachments();
  }

  getProjectFolders() {
    const documentsCache = {};
    // This method is called every time we load the tree from scratch. That's why we need to clean the cache here - for it to be relevant for the current tree.
    this.getFolderContentsWrapped = createGetFolderContentsMethod(
      documentsCache,
      this.props.fetchFolderContents
    );
    this.hasMoreFolderContentsWrapped = createHasMoreFolderContents(documentsCache);

    return new Promise((resolve, reject) => {
      this.props.fetchProjectFolders(resolve, reject);
    });
  }

  getFolderContents(urn) {
    return this.getFolderContentsWrapped(urn).then((data) =>
      filterCurrentDocument(data, this.props.issue.targetUrn)
    );
  }

  hasMoreFolderContents(context) {
    return this.hasMoreFolderContentsWrapped(context);
  }

  handleChoosingDocument(documentUrn, document) {
    const { issue, createAttachmentFromDocument } = this.props;

    this.setState({ isSaving: true }, () => {
      createAttachmentFromDocument(document, issue.id, attachmentOrigin.ATTACHMENT_TAB);
      this.setState({ isSaving: false });
    });
  }

  handleUploadFiles(files) {
    const { issue, uploadAttachmentFiles } = this.props;

    this.setState({ isSaving: true }, () => {
      uploadAttachmentFiles(
        [...files].map((file) => {
          return { id: getUUID(), file };
        }),
        issue.id,
        true,
        attachmentOrigin.ATTACHMENT_TAB
      );
      this.setState({ isSaving: false });
    });
  }

  renderFilePicker() {
    const { container, attachments } = this.props;

    return (
      <FilePicker
        container={container}
        allowUploadMultipleFiles // Allow upload multiple files.
        multi={false} // Don't allow multiple Docs documents.
        isSavingDocument={this.state.isSaving}
        allowDocsDocuments
        acceptFileTypes={getAllowedFiles()}
        openDocsUrn={this.handleChoosingDocument}
        uploadFiles={this.handleUploadFiles}
        getRootFolders={this.getProjectFolders}
        getFolderContents={this.getFolderContents}
        hasMoreFolderContents={this.hasMoreFolderContents}
        styleType={attachments.length ? FilePicker.STYLE.BAR : FilePicker.STYLE.LARGE}
      />
    );
  }

  // eslint-disable-next-line consistent-return
  renderAttachments() {
    const { issue, attachments } = this.props;
    const canAddAttachments = canAddAttachment(issue);

    if (attachments.length) {
      return <IssueAttachmentsList attachments={attachments} />;
    }
    if (!canAddAttachments) {
      // We render empty state only if no attachments and user cannot upload any.
      // If user can upload - we render `FilePicker`.
      return (
        <EmptyState
          icon={EmptyState.Icons.EMPTY_TEMPLATE}
          text={T.translate('attachments.noAttachments')}
        />
      );
    }
  }

  render() {
    const { issue, isLoading, attachments, viewType } = this.props;

    if (isLoading && attachments.length === 0) {
      return <Loader />;
    }

    const canAddAttachments = canAddAttachment(issue);

    const contentClasses = classNames('IssuePreviewAttachmentsTab__content', {
      'IssuePreviewAttachmentsTab__content--empty': attachments.length === 0,
      'IssuePreviewAttachmentsTab__content--narrow': viewType === viewTypes.DOCUMENT,
      'IssuePreviewAttachmentsTab__content--wide': viewType === viewTypes.PROJECT,
    });

    return (
      <div className="IssuePreviewAttachmentsTab" data-testid="IssuePreviewAttachmentsTab">
        <Dropzone
          onDrop={this.handleUploadFiles}
          accept={getAllowedFiles()}
          disabled={!canAddAttachments}
        >
          <div className={contentClasses}>
            {canAddAttachments ? this.renderFilePicker() : null}
            {this.renderAttachments()}
          </div>
        </Dropzone>
      </div>
    );
  }
}

IssuePreviewAttachmentsTab.displayName = 'IssuePreviewAttachmentsTab';

IssuePreviewAttachmentsTab.propTypes = {
  issue: issueAttributesType,
  isLoading: PropTypes.bool.isRequired,
  attachments: PropTypes.array,
  fetchAttachmentsAction: PropTypes.func.isRequired,
  clearFailedUploadingAttachments: PropTypes.func.isRequired,
  fetchProjectFolders: PropTypes.func.isRequired,
  fetchFolderContents: PropTypes.func.isRequired,
  uploadAttachmentFiles: PropTypes.func.isRequired,
  createAttachmentFromDocument: PropTypes.func.isRequired,
  container: PropTypes.object.isRequired,
  viewType: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => {
  const attachments = getAttachmentsList(state);
  const uploadingAttachments = getUploadingAttachments(state);
  const fullAttachmentList = uploadingAttachments.concat(attachments);

  return {
    isLoading: isLoadingAttachments(state),
    attachments: fullAttachmentList,
    viewType: getCurrentViewType(state),
  };
};

const mapDispatchToProps = {
  fetchAttachmentsAction,
  fetchProjectFolders,
  fetchFolderContents,
  createAttachmentFromDocument,
  clearFailedUploadingAttachments,
  uploadAttachmentFiles: AttachmentUploadThunk.uploadAttachmentFiles,
};

export default connect(mapStateToProps, mapDispatchToProps)(IssuePreviewAttachmentsTab);
